import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeHigh } from '@fortawesome/pro-light-svg-icons/faVolumeHigh';
import { faVolumeXmark } from '@fortawesome/pro-light-svg-icons/faVolumeXmark';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';
import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';
import { TypedMemo } from '../../common/types';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import { publicShareKindOf } from './assetShareFacts';
import { assetShareMessages } from './assetShareMessages';
import { AssetSharePreviewChip } from './AssetSharePreviewChip';
import { ListenButton, OpenInReleaseButton } from './AssetShareActions';
import type { OpenInRelease } from './AssetShareActions';
import { AssetShareFoot } from './AssetShareGrowth';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The top of the Story (design asset-share c6): the artist, the Preview chip and the sound button. */
const StoryTopComponent: React.FC<{ share: PublicAssetShare; muted: boolean; onToggleMute: () => void }> = ({
  share,
  muted,
  onToggleMute,
}) => {
  const intl = useIntl();
  const avatar = share.artist.avatarUrl ?? share.song.artworkUrl;
  return (
    <div className={styles.storyTop}>
      <span className={styles.artistChip}>
        {avatar !== null && <img className={styles.avatar} src={getFullAPIFileURL(avatar)} alt="" />}
        {share.artist.name}
      </span>
      <span className={styles.storyTopEnd}>
        {share.preview && <AssetSharePreviewChip />}
        <button
          type="button"
          className={clsx(styles.glassButton, styles.iconButton)}
          onClick={onToggleMute}
          aria-label={intl.formatMessage(muted ? assetShareMessages.soundOn : assetShareMessages.soundOff)}
          aria-pressed={!muted}
          data-testid="asset-share-sound"
        >
          <FontAwesomeIcon icon={muted ? faVolumeXmark : faVolumeHigh} />
        </button>
      </span>
    </div>
  );
};

export const StoryTop = TypedMemo(StoryTopComponent);

/**
 * The bottom of the Story (design asset-share c6), on a scrim: the kind, the song, the artist,
 * Listen and Share in one row, then the Music24 line. There is no Download action (owner decision
 * 2026-10-19).
 */
const StoryBottomComponent: React.FC<{
  share: PublicAssetShare;
  onShare: () => void;
  openInRelease: OpenInRelease | null;
}> = ({ share, onShare, openInRelease }) => {
  const intl = useIntl();
  return (
    <div className={styles.storyBottom}>
      <div>
        <span className={styles.eyebrow}>
          <span className={styles.eyebrowDot} aria-hidden />
          {share.assetLabel}
        </span>
        <h1 className={clsx(styles.song, styles.storySong)}>{share.song.title}</h1>
        <p className={styles.storyArtist}>{share.artist.name}</p>
      </div>
      <div className={styles.storyRow}>
        <ListenButton share={share} className={styles.listenStory} />
        <button
          type="button"
          className={clsx(styles.glassButton, styles.iconButtonLarge)}
          onClick={onShare}
          aria-label={intl.formatMessage(assetShareMessages.share)}
          data-testid="asset-share-share"
        >
          <FontAwesomeIcon icon={faArrowUpFromBracket} />
        </button>
      </div>
      {openInRelease !== null && <OpenInReleaseButton openInRelease={openInRelease} onMedia />}
      <AssetShareFoot kind={publicShareKindOf(share.assetLabel)} onMedia />
    </div>
  );
};

export const StoryBottom = TypedMemo(StoryBottomComponent);
